/**
 * @file Settings-owned editor for Drugs notification role targets.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";

const DRUG_NOTIFICATION_TARGET_EVENTS = Object.freeze([
    {
        id: "drug_low_stock_reminder",
        label: "Low Stock Reminder (Yellow)",
        description: 'Stock falls below "Reminder" threshold',
    },
    {
        id: "drug_low_stock_warning",
        label: "Low Stock Warning (Red)",
        description: 'Stock falls below "Warning" threshold',
    },
    {
        id: "drug_negative_stock",
        label: "Negative Stock Alert",
        description: "Stock level drops below zero",
    },
    {
        id: "drug_audit_discrepancy",
        label: "Audit Discrepancy",
        description: "Audit finalised with count mismatches",
    },
    {
        id: "drug_audit_overdue",
        label: "Audit Overdue",
        description: "Scheduled audit date passed",
    },
]);

function escapeAttribute(value = "") {
    return sanitizeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/**
 * Ensures Drugs notification target config exists in the editable settings
 * draft and returns it.
 *
 * @returns {Record<string, string[]>} Notification event to role ID mapping.
 */
function getEditableNotificationRoleTargets() {
    if (!tempConfigData.drugs) {tempConfigData.drugs = {};}
    if (!tempConfigData.drugs.notificationRoleTargets) {
        tempConfigData.drugs.notificationRoleTargets = {};
    }

    return tempConfigData.drugs.notificationRoleTargets;
}

/**
 * Renders the Drugs alert target settings section.
 *
 * @returns {string} Section HTML.
 */
export function renderDrugNotificationTargetSettingsSection() {
    const targets = getEditableNotificationRoleTargets();
    const roles = [...(appData.drugRoles || [])]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    if (roles.length === 0) {
        return `
            <div class="bg-white rounded-lg shadow overflow-hidden mt-8">
                <div class="bg-white px-4 py-3 border-b">
                    <h4 class="font-semibold text-gray-700">Drugs Alert Targets</h4>
                    <p class="text-xs text-gray-500 mt-1">
                        Select which Drugs roles receive role-routed system alerts.
                    </p>
                </div>
                <div class="p-6 text-center text-gray-400 text-sm">
                    No Drugs roles configured.
                </div>
            </div>
        `;
    }

    return `
        <div class="drug-notification-target-section bg-white rounded-lg shadow overflow-hidden mt-8">
            <div class="bg-white px-4 py-3 border-b">
                <h4 class="font-semibold text-gray-700">Drugs Alert Targets</h4>
                <p class="text-xs text-gray-500 mt-1">
                    Select which Drugs roles receive role-routed system alerts.
                    Transaction approvals are routed by Drugs workflow settings.
                </p>
            </div>
            <div class="drug-notification-target-wrapper overflow-x-auto">
                <table class="drug-notification-target-table w-full text-sm text-left">
                    <thead class="drug-notification-target-head bg-gray-50 text-gray-500 font-medium border-b">
                        <tr>
                            <th class="py-3 px-4 w-1/3">Alert Type</th>
                            ${roles.map((role) => `
                                <th class="py-3 px-4 text-center whitespace-nowrap">
                                    ${sanitizeHTML(role.name || "Unnamed Role")}
                                </th>
                            `).join("")}
                        </tr>
                    </thead>
                    <tbody class="drug-notification-target-body">
                        ${DRUG_NOTIFICATION_TARGET_EVENTS.map((event) => {
        const currentTargets = targets[event.id] || [];
        return `
                            <tr class="drug-notification-target-row hover:bg-gray-50">
                                <td class="drug-notification-alert-cell py-3 px-4">
                                    <div class="font-medium text-gray-900">
                                        ${sanitizeHTML(event.label)}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        ${sanitizeHTML(event.description)}
                                    </div>
                                </td>
                                ${roles.map((role) => `
                                    <td class="drug-notification-role-cell py-3 px-4 text-center" data-mobile-label="${escapeAttribute(role.name || "Unnamed Role")}">
                                        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} justify-center" data-native-checkbox-label>
                                            <input
                                                type="checkbox"
                                                class="${NATIVE_CHECKBOX_CLASSES} drug-notification-target-check"
                                                data-event="${escapeAttribute(event.id)}"
                                                data-role-id="${escapeAttribute(role.id)}"
                                                ${currentTargets.includes(role.id) ? "checked" : ""}
                                            >
                                            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                                                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                                            </span>
                                        </label>
                                    </td>
                                `).join("")}
                            </tr>
                        `;
    }).join("")}
                    </tbody>
                </table>
            </div>
        </div>
    `;
}

/**
 * Binds Drugs alert target setting changes within the Notifications tab.
 *
 * @param {HTMLElement} container Notifications tab container.
 * @param {Function} updateSaveButton Dirty-state callback.
 * @returns {void}
 */
export function bindDrugNotificationTargetSettings(
    container,
    updateSaveButton,
) {
    container.querySelectorAll(".drug-notification-target-check")
        .forEach((checkbox) => {
            checkbox.addEventListener("change", (event) => {
                const input = event.target;
                const eventKey = input.dataset.event;
                const roleId = input.dataset.roleId;
                const targets = getEditableNotificationRoleTargets();

                if (!targets[eventKey]) {targets[eventKey] = [];}

                let roleTargets = targets[eventKey];
                if (input.checked && !roleTargets.includes(roleId)) {
                    roleTargets.push(roleId);
                } else if (!input.checked) {
                    roleTargets = roleTargets.filter((id) => id !== roleId);
                }

                targets[eventKey] = roleTargets;
                updateSaveButton(true);
            });
        });
}
